import { useEffect, useRef } from "react";
import { Platform } from "react-native";
import * as Device from "expo-device";
import { useAuthContext } from "@/features/auth/hooks/use-auth-context";
import { logger } from "@/shared/lib/logger";
import { posthog } from "@/shared/lib/posthog";
import { useRegisterPushToken } from "../api/push-tokens";
import {
  addPushTokenRefreshListener,
  registerForPushNotificationsAsync,
} from "../lib/push";
import { setCachedPushToken } from "../lib/token-cache";

/**
 * Registers this device for push notifications once per app launch while a
 * user is signed in: asks for OS permission (first launch only), fetches the
 * Expo push token and persists it via the BFF. Also keeps the server row in
 * sync when FCM/APNs rotates the token.
 */
export function usePushRegistration() {
  const { session } = useAuthContext();
  const { mutate: registerToken } = useRegisterPushToken();
  const hasRequested = useRef(false);

  const userId = session?.user.id;

  useEffect(() => {
    if (!userId || hasRequested.current || Platform.OS === "web") return;
    hasRequested.current = true;

    const platform = Platform.OS as "ios" | "android";
    const deviceName = Device.deviceName ?? null;

    const register = () =>
      registerForPushNotificationsAsync()
        .then((token) => {
          if (!token) {
            posthog?.capture("push_permission_unavailable");
            return;
          }
          setCachedPushToken(token);
          registerToken({ token, platform, deviceName });
        })
        .catch((error) => {
          logger.warn("Push registration failed", { error: String(error) });
        });

    register();

    // On rotation, re-derive the Expo push token rather than forwarding the
    // native device token — the BFF only accepts ExponentPushToken[...] values.
    return addPushTokenRefreshListener(register);
  }, [userId, registerToken]);
}
